import React, {useEffect} from "react";
import {FormControlLabel, Grid, IconButton, LinearProgress, Paper, Radio, RadioGroup, Typography, withTheme} from "@material-ui/core";
import {ArrowBack} from "@material-ui/icons";
import {useGlobalStyles} from "../../catalogmanager/utils/styles";
import {makeStyles} from "@material-ui/core/styles";
import {StorageHelper} from "../../helpers";
import EnhancedErrorDialog from "../../catalogmanager/components/EnhancedErrorDialog";
import EnhancedSelect from "../../catalogmanager/components/EnhancedSelect";
import FilterBlock from "../../catalogmanager/containers/export/components/FilterBlock";
import {utilExportInventory, utilGetPartners} from "../../catalogmanager/utils/utils";
import ContentLoader from "../../general/content_loader";
import DownloadButton from "../../general/download_button";
import {fileTypes} from "../../catalogmanager/containers/export/advanced_export";

//region Styles and StyledComponents
export const useAdvancedExportStyles = makeStyles(theme => ({
    sectionTitle: {
        fontSize: "20px",
        fontWeight: 700,
    },
    filterHeader: {
        color: "#7F7D83",
        fontWeight: 700
    }
}));
//endregion

const blankProfile = {
    Name: '',
    Delimiter: ",",
    FileType: "inventoryFeed",
    OutputFormat: "csv",
    Partner: "",
    Filter: undefined,
    Fields: [],
    Rules: []
};

const storedIdKey = "LBInventoryExportPartnerId";

function InventoryManagementExport(props) {
    const globalClasses = useGlobalStyles();
    const classes = useAdvancedExportStyles();
    const storageHelper = new StorageHelper();

    //region Component State
    const [error, setError] = React.useState({open: false, errorHeader: "", errorMessage: ""});
    const [loading, setLoading] = React.useState(false);
    const [updating, setUpdating] = React.useState(false);

    const [partnerId, setPartnerId] = React.useState(undefined);
    const [partners, setPartners] = React.useState([]);
    const [profile, setProfile] = React.useState(blankProfile);

    //endregion

    //region Functions
    const exportItems = async () => {
        const fileType = profile.FileType;
        const format = profile.OutputFormat;
        const matched = fileType === "retailerFeed" || fileType === "inventoryFeed";
        const transform = fileType === "retailerFeed";
        const modifiedAfter = profile.ModifiedAfter;
        const filter = profile.Filter;

        setUpdating(true);
        try {
            const response = await utilExportInventory(partnerId, format, matched, transform, modifiedAfter, filter);
            if (response.errorMessage) {
                setError({...error, open: true, errorHeader: "Error", errorMessage: response.errorMessage});
                return null;
            }
            return response;
        } catch (err) {
            console.error("Export error:", err);
            setError({
                ...error,
                open: true,
                errorHeader: "Error",
                errorMessage: err.message || "An unexpected error occurred during export"
            });
            return null;
        } finally {
            setUpdating(false);
        }
    }

    const loadPartners = async () => {
        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;

        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        if(props.isRetailer) {
            sortedPartners.unshift({
                Id: "all",
                CompanyName: "All Partners"
            });
        }
        setPartners(sortedPartners);

        // Get stored partner ID or default to first partner
        let storedId = storageHelper.get(storedIdKey);
        if (!storedId || !sortedPartners.find(p => p.Id.toString() === storedId.toString())) {
            storedId = sortedPartners[0].Id;
            storageHelper.set(storedIdKey, storedId);
        }

        setPartnerId(storedId);
        setProfile({...profile, Partner: sortedPartners.find(p => p.Id === storedId).CompanyName});
        setLoading(false);
    }
    //endregion

    useEffect(() => {
        loadPartners();

        // eslint-disable-next-line
    }, []);

    return (
        <Grid>
            <LinearProgress
                classes={{
                    colorPrimary: globalClasses.progressBar,
                    barColorPrimary: globalClasses.progressBarBackground
                }}
                style={{
                    width: "calc(100% + 2em)",
                    height: '0.4em',
                    position: "relative",
                    top: "-1em",
                    right: "1em",
                    visibility: updating ? undefined : 'hidden'
                }}
                type='indeterminate'/>

            <Grid container style={{gap: "24px", padding: "24px 14px"}}>
                <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                    <IconButton className={globalClasses.iconButton}
                                style={{height: "36px", width: "36px"}}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (props.location.key === undefined)
                                        props.history.push("/advanced-product-management/v2");
                                    else
                                        props.history.goBack();
                                }}>
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>

                    <Typography style={{fontSize: "24px", fontWeight: "600"}}>
                        Exports
                    </Typography>
                </Grid>

                <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container>
                            <Typography className={classes.sectionTitle}>
                                Partner
                            </Typography>
                        </Grid>

                        <Grid item container style={{gap: "12px", flexDirection: "column", textAlign: "left"}}>
                            <EnhancedSelect
                                isClearable
                                loading={loading}
                                value={{value: profile.Partner, label: profile.Partner}}
                                options={partners.map(partner => ({
                                    value: partner.CompanyName,
                                    label: partner.CompanyName
                                }))}
                                onChange={(e) => {
                                    if (e == null) {
                                        setPartnerId("");
                                        setProfile({...profile, Partner: ""});
                                        return;
                                    }

                                    const selectedPartner = partners.find(partner => partner.CompanyName === e.value);
                                    if (selectedPartner !== undefined) {
                                        setPartnerId(selectedPartner.Id);
                                        setProfile({...profile, Partner: selectedPartner.CompanyName});
                                    }
                                }}
                            />

                            {(partnerId === undefined || partnerId === "") && (
                                <Typography>
                                    Please select a partner to export products from.
                                </Typography>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>

                        <Grid item container>
                            <Typography className={classes.sectionTitle} style={{margin: 0}}>
                                Filters
                            </Typography>
                        </Grid>
                        {loading ? (
                            <ContentLoader preserveAspectRatio='none' style={{height: '6em', width: '100%'}}
                                           primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        ) : (
                            <FilterBlock classes={classes} isRetailer={props.isRetailer}
                                         type="inventoryManagement" attributes={[]}
                                         profile={profile} setProfile={setProfile}
                                         noOptions={true}             
                            />
                        )}
                    </Paper>
                </Grid>

                <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container style={{marginBottom: "16px"}}>
                            <Typography className={classes.sectionTitle}>
                                Feed Type
                            </Typography>
                        </Grid>

                        <Grid item container style={{textAlign: "left"}}>
                            <RadioGroup
                                value={profile.FileType}
                                onChange={(e) => setProfile({...profile, FileType: e.target.value})}
                                style={{display: "flex", flexDirection: "column", gap: "16px"}}
                            >
                                <FormControlLabel
                                    value="inventoryFeed"
                                    control={<Radio/>}
                                    label={
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Typography style={{fontWeight: 500}}>
                                                Inventory Feed
                                            </Typography>
                                            <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                This will pull a file with all present items in Logicbroker that are
                                                being shared with the retailer and will contain all the
                                                Logicbroker-available fields.
                                            </Typography>
                                        </Grid>
                                    }
                                />
                                <FormControlLabel
                                    value="retailerFeed"
                                    control={<Radio/>}
                                    label={
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Typography style={{fontWeight: 500}}>
                                                Retailer Feed
                                            </Typography>
                                            <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                This will pull a file with all present items in Logicbroker that are
                                                being shared with the retailer and will contain only retailer-specific
                                                fields.
                                            </Typography>
                                        </Grid>
                                    }
                                />
                                <FormControlLabel
                                    value="unmatchedSkuFeed"
                                    control={<Radio/>}
                                    label={
                                        <Grid container style={{flexDirection: "column"}}>
                                            <Typography style={{fontWeight: 500}}>
                                                Unmatched SKU Feed
                                            </Typography>
                                            <Typography style={{fontWeight: 400, color: "#7F7D83"}}>
                                                This will pull a file with all items that do not have a Retailer SKU
                                                associated with the Supplier SKU.
                                            </Typography>
                                        </Grid>
                                    }
                                />
                            </RadioGroup>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item container xs={12} justify={"flex-start"} style={{gap: "16px"}}>
                    <Paper style={{width: "100%", borderRadius: "5px", padding: "24px"}}>
                        <Grid item container style={{marginBottom: "16px"}}>
                            <Typography className={classes.sectionTitle}>
                                Export Settings
                            </Typography>
                        </Grid>

                        <Grid item container style={{gap: "12px", textAlign: "left"}}>
                            <Grid container alignItems={"center"} style={{gap: "45px"}}>
                                <Typography style={{width: "200px", fontSize: "16px", fontWeight: 400}}>
                                    Export Format
                                </Typography>

                                <EnhancedSelect
                                    options={fileTypes}
                                    value={fileTypes.find(r => r.value === profile.OutputFormat)}
                                    onChange={(e) => setProfile({...profile, OutputFormat: e.value})}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item container xs={12} justify={"flex-end"}>
                    <DownloadButton
                        variant='contained'
                        color='primary'
                        onClick={async () => {
                            const response = await exportItems();
                            return response;
                        }}
                        disabled={updating}
                    >
                        Export
                    </DownloadButton>
                </Grid>
            </Grid>

            {/*region Dialogs and Alerts*/}
            <EnhancedErrorDialog open={error.open} errorHeader={error.errorHeader} errorMessage={error.errorMessage}
                                 onClose={() => {
                                     setError({...error, open: false});
                                     if (loading) props.history.goBack();
                                 }}/>
            {/*endregion*/}
        </Grid>
    )
}

export default withTheme(InventoryManagementExport);
